import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, MailCheck, Send } from "lucide-react";
import customerApi from "./customerApi";

/* Shown until a new account confirms the 6-digit code we emailed them.
   Order history / invoices stay locked until then. */
export function VerifyEmailBanner({ me, onVerified }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  if (!me || me.email_verified !== false) return null;

  const verify = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await customerApi.post("/customer/verify-email", { code: code.trim() });
      toast.success("Email verified , welcome to Heritage HQ!");
      onVerified?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That code didn't work.");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      await customerApi.post("/customer/resend-verification");
      toast.success("New code sent , check your inbox (and spam).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resend just now.");
    } finally { setResending(false); }
  };

  return (
    <div
      data-testid="verify-email-banner"
      className="mb-5 rounded-md border-2 border-dashed p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3"
      style={{ borderColor: "#C49247", backgroundColor: "#FFF9EC" }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#8A6320] inline-flex items-center gap-1.5">
          <MailCheck className="w-3.5 h-3.5" /> Verify your email
        </div>
        <p className="text-sm text-[#3C2A1E] mt-1">
          We emailed a 6-digit code to <strong>{me.email}</strong>. Enter it here to unlock your
          order history and invoices.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="123456"
          inputMode="numeric"
          data-testid="verify-code-input"
          className="w-28 px-3 py-2 text-center tracking-[0.3em] font-semibold bg-white border border-[#C49247]/50 rounded text-[#3C2A1E] outline-none focus:border-[#C49247]"
        />
        <button
          type="button"
          onClick={verify}
          disabled={busy || code.length < 6}
          data-testid="verify-code-submit"
          className="h-10 px-4 text-xs tracking-[0.2em] uppercase font-semibold rounded bg-[#6E1E3A] hover:bg-[#5a1830] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Verify
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={resending}
          data-testid="verify-code-resend"
          className="h-10 px-3 text-[11px] underline underline-offset-2 text-[#8A6320] disabled:opacity-50"
        >
          {resending ? "Sending…" : "Resend code"}
        </button>
      </div>
    </div>
  );
}
